import { useEffect, useState } from "react";
import { api, formatApiError, uploadWebsiteImage } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  category: "Wedding",
  description: "",
  price: 0,
  features: [],
  duration: "",
  image_data: "",
};

export default function AdminPackages() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | id | 'new'
  const [form, setForm] = useState(EMPTY);
  const [featuresStr, setFeaturesStr] = useState("");
  const [categories, setCategories] = useState([]);

  const load = () => api.get("/packages").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/master/package-categories")
      .then((r) => setCategories(r.data || []))
      .catch(() => setCategories([]));
  }, []);

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name,
      category: p.category,
      description: p.description,
      price: p.price,
      features: p.features || [],
      duration: p.duration || "",
      image_data: p.image_data || "",
    });
    setFeaturesStr((p.features || []).join("\n"));
  };
  const startNew = () => {
    setEditing("new");
    setForm({ ...EMPTY, category: categories[0]?.name || EMPTY.category });
    setFeaturesStr("");
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: Number(form.price),
      features: featuresStr.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") await api.post("/packages", payload);
      else await api.put(`/packages/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await api.delete(`/packages/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-packages" className="p-10">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="overline mb-2">Offerings</div>
          <h1 className="font-serif-display text-4xl">Packages</h1>
        </div>
        <button
          data-testid="add-package-btn"
          onClick={startNew}
          className="bg-white text-black px-6 py-3 text-sm uppercase tracking-[0.2em]"
        >
          + New package
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((p) => (
          <div
            key={p.id}
            data-testid={`pkg-${p.id}`}
            className="bg-[#0a0a0c] border border-white/10 p-6 rounded-sm"
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="overline text-[#E11D2A]">{p.category}</div>
                <h3 className="font-serif-display text-2xl mt-2">{p.name}</h3>
              </div>
              <div className="font-serif-display text-2xl">₹{p.price}</div>
            </div>
            <p className="text-sm text-neutral-400 mt-3 leading-relaxed">
              {p.description}
            </p>
            <div className="mt-6 flex gap-4">
              <button
                data-testid={`pkg-edit-${p.id}`}
                onClick={() => startEdit(p)}
                className="text-xs uppercase tracking-widest hover-underline"
              >
                Edit
              </button>
              <button
                data-testid={`pkg-delete-${p.id}`}
                onClick={() => remove(p.id)}
                className="text-xs uppercase tracking-widest text-red-300"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div
          data-testid="pkg-modal"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-[#0a0a0c] border border-white/10 rounded-sm p-8 max-h-[90vh] overflow-y-auto"
          >
            <h2 className="font-serif-display text-3xl mb-6">
              {editing === "new" ? "New package" : "Edit package"}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Name</label>
                <input
                  required
                  data-testid="pkg-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="line-input w-full py-3"
                />
              </div>
              <div>
                <label className="overline block mb-2">Category</label>
                <select
                  data-testid="pkg-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="line-input w-full py-3"
                >
                  {categories.map((c) => (
                    <option key={c.id || c.name} value={c.name} className="bg-[#0a0a0c]">{c.name}</option>
                  ))}
                  {form.category && !categories.some((c) => c.name === form.category) && (
                    <option value={form.category} className="bg-[#0a0a0c]">{form.category}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Price (₹)</label>
                <input
                  required
                  type="number"
                  data-testid="pkg-price"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="line-input w-full py-3"
                />
              </div>
              <div>
                <label className="overline block mb-2">Duration</label>
                <input
                  data-testid="pkg-duration"
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  className="line-input w-full py-3"
                />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Service Photo (max 10MB)</label>
                <input type="file" accept="image/*" onChange={async(e)=>{const f=e.target.files?.[0]; if(!f)return; if(f.size>10*1024*1024){toast.error("Image must be 10MB or smaller");return;} try{const media=await uploadWebsiteImage(f,"service");setForm({...form,image_data:media.image_url,image_key:media.image_key});toast.success("Service image optimized");}catch(err){toast.error(formatApiError(err));}}} className="text-sm text-neutral-400"/>
                {form.image_data && <img src={form.image_data} alt="Service preview" className="mt-3 h-36 w-56 object-cover rounded"/>}
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Description</label>
                <textarea
                  rows={3}
                  data-testid="pkg-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="line-input w-full py-3 resize-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Features (one per line)</label>
                <textarea
                  rows={5}
                  data-testid="pkg-features"
                  value={featuresStr}
                  onChange={(e) => setFeaturesStr(e.target.value)}
                  className="line-input w-full py-3 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button
                data-testid="pkg-save"
                type="submit"
                className="flex-1 bg-white text-black py-3 text-sm uppercase tracking-[0.2em]"
              >
                Save
              </button>
              <button
                type="button"
                data-testid="pkg-cancel"
                onClick={() => setEditing(null)}
                className="px-8 py-3 text-sm uppercase tracking-[0.2em] border border-white/20"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
